import { api, apiv2 } from "./api";
import { AxiosError, AxiosResponse } from 'axios';
import { AuthBodyType } from "./types";
import { ChecklistReportType } from "./Reports";
import { toast } from "react-toastify";
import type { TicketStatus } from "../types/tickets/types";

export type objectModel = {
  path: string;
  nome: string;
  type: string;
};

export type PostExecutionTicketPayload = {
  requestId: string;
  checklistId: number;
  formAnonimoId?: string | null;
  itemId?: string | null;
  subitemId?: string | number | null;
  ocorrenciaId?: string | number | null;
  modeloId: string;
  instrucao: string;
  prioridade: "baixa" | "media" | "alta";
  dataPrevisao: number;
  hierarquia?: {
    cliente?: string;
    local?: string;
    conjunto?: string;
    unidade?: string;
  } | null;
};

export type TicketsType = {
  checklistId: number;
  itemId: string;
  cliente: string;
  conjunto: string;
  dataMudancaStatus: number;
  dataPrevisao: number;
  dataPrevisaoFormatada: string;
  empresa: string;
  expirado: boolean;
  gerencia: string;
  grupoNotificacao: number;
  id: string;
  inicio: number;
  inicioFormatado: string;
  instrucao: string;
  local: string;
  nome: string;
  nomeCliente: string;
  nomeConjunto: string;
  nomeGerencia: string;
  nomeLocal: string;
  nomeUnidade: string;
  status: TicketStatus;
  statusClass: string;
  statusFormatado: string;
  unidade: string;
  userMudancaStatus: string;
  usuario: string;
  usuarioNome: string;
  subitemId: number;
  ocorrenciaId?: string | number | null;
  formAnonimoId?: string | null;
  execucaoChecklist: ChecklistReportType;
  motivoEncaminhamento: string;
  historicoChamado?: {
    [key: string]: {
      comentario: string;
      data: number;
      dataFormatada: string;
      dataMensagem: number;
      dataMensagemFormatada: string;
      id: string;
      idChamado: string;
      nomeUsuarioAcompanhamento: string;
      status: TicketStatus;
      statusClass: string;
      statusTraduzido: string;
      usuarioAcompanhamento: string;
      foto: string;
    };
  };
  encaminha: boolean;
  block?: boolean;
  blockRefuse?: boolean;
  responsavel?: string;
  responsavelNome?: string;
  acoesDisponiveis?: Record<string, boolean>;
};

export type novaMensagem = {
  comentario: string;
  dataAcompanhamento: number | Date;
  status: TicketStatus;
  referenciaFoto?: string[];
  referenciaArquivo?: objectModel[];
};

export type filtrosType = {
  userEmissor: string[] | null; //(array de id de usuario),
  expirado: boolean | null;
  modelos: number[] | null; //(array de id de modelos de chamado antigo),
  modelosNovos: string[] | null; //(array de id de modelos de chamado2.0),
  inicioRange: {
    de: number | null;
    ate: number | null;
  } | null;
  previsaoRange: {
    de: number | null;
    ate: number | null;
  } | null;
  status?: string[] | null;
  cliente: string[] | null; //(nome do cliente),
  local: string[] | null; //(nome do local),
  conjunto: string[] | null; //(nome do conjunto),
  unidade: string[] | null; //(nome da unidade),//(array de status possiveis de chamado)
  ordenarPor: string | null;
  ordem: boolean | null;
  responsavel: string[] | null;
  duplicado: boolean | null;
} | null;

export type RequesterTicketStatus = TicketStatus;

export type RequesterTicketsFilters = {
  modelos?: string[];
  status?: RequesterTicketStatus[];
  range?: {
    key: "abertura" | "previsao" | "execucao";
    de: number;
    ate: number;
  };
};

export type RequesterTicket = Pick<
  TicketsType,
  "id" | "inicio" | "nome" | "responsavel" | "responsavelNome" | "dataPrevisao"
> & {
  status: RequesterTicketStatus;
  prioridade?: string;
};

export type RequesterTicketsResponse = {
  success: boolean;
  data: {
    chamados: RequesterTicket[];
    lastIndex: string | null;
    totalItens: number;
  };
};

export type RejectedTicketPayload = {
  modeloId: string;
  tipoServico?: string;
  prioridade?: string;
  instrucao?: string;
  cliente?: string;
  local?: string;
  conjunto?: string;
  unidade?: string;
};

export class TicketsV3 {
  postExecution = async (token: AuthBodyType, payload: PostExecutionTicketPayload) => {
    const response = await apiv2.post("/chamados/posExecucao", {
      ...token,
      ...payload,
    });
    return response.data.data;
  };

  getAll = async (
    body: AuthBodyType,
    itensPerPage: number,
    pageNumber: number,
    mobileFilter?: filtrosType,
    emitidos?: boolean,
    lastIndex?: number | string | null,
  ) => {
    try {
      const data = await apiv2.post("chamados/v2", {
        ...body,
        itensPerPage,
        pageNumber,
        ...(lastIndex !== null && lastIndex !== undefined ? { lastIndex } : {}),
        ...(typeof emitidos === "boolean" ? { emitidos } : {}),
        ...(mobileFilter ? { mobileFilter } : {}),
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        toast.error(error.message);
        throw error.response?.status;
      }
    }
  };

  getByRequester = async (
    body: AuthBodyType,
    id: string,
    last?: string | null,
    limit?: number,
    filters?: RequesterTicketsFilters
  ): Promise<RequesterTicketsResponse | undefined> => {
    try {
      const data = await apiv2.post("chamados/getByRequester", {
        ...body,
        id,
        ...(last ? { last } : {}),
        ...(limit ? { limit } : {}),
        ...(filters ? { filters } : {})
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        toast.error(error.message);
        throw error.response?.status;
      }
    }
  };

  createFromRejected = async (
    body: AuthBodyType,
    id: string,
    ticket: RejectedTicketPayload,
  ) => {
    try {
      const data = await apiv2.post(`chamados/${id}/reject/new-ticket`, {
        ...body,
        ticket,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        toast.error(error.message);
        throw error.response?.status;
      }
    }
  };

  acceptRejected = async (body: AuthBodyType, id: string) => {
    try {
      const data = await apiv2.post(`chamados/${id}/reject/accept`, {
        ...body,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        toast.error(error.message);
        throw error.response?.status;
      }
    }
  };

  disputeRejected = async (body: AuthBodyType, id: string, comentario: string) => {
    try {
      const data = await apiv2.post(`chamados/${id}/reject/dispute`, {
        ...body,
        comentario,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        toast.error(error.message);
        throw error.response?.status;
      }
    }
  };

  action = async (
    body: AuthBodyType,
    id: string,
    param: string,
    comentario?: string,
    prioridade?: string,
    dataAcompanhamento?: number,
    referenciaFoto?: string,
    referenciaArquivo?: string,
    motivo?: string,
  ) => {
    try {
      const data = await apiv2.post(`chamados/${id}/${param}`, {
        ...body,
        comentario,
        prioridade,
        dataAcompanhamento,
        referenciaFoto,
        referenciaArquivo,
        motivo,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        toast.error(error.message);
        throw error.response?.status;
      }
    }
  };
}

export class TicketsV2 {
  getChamados = async (
    body: AuthBodyType,
    itensPerPage: number,
    pageNumber: number,
    mobileFilter: filtrosType | null,
    emitidos: boolean,
    lastIndex: number
  ) => {
    try {
      const data = await api.post(process.env.REACT_APP_AXYMA_MICROSSERVICES_APIS_URL + "/chamados", {
        ...body,
        itensPerPage,
        pageNumber,
        mobileFilter,
        emitidos,
        lastIndex,
      });
      return data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        toast.error(error.message);
        throw error.response?.status;
      }
    }
  };
};

class Tickets {
  getAll = async (
    body: AuthBodyType,
    itensPerPage: number,
    pageNumber: number,
    mobileFilter: filtrosType | null,
    emitidos: boolean,
    lastIndex: number
  ) => {
    const data = await api.post("/chamados/getAll/v2", {
      ...body,
      itensPerPage,
      pageNumber,
      mobileFilter,
      emitidos,
      lastIndex,
    });
    return data.data;
  };

  getAllGestor = async (body: AuthBodyType) => {
    try {
      const data: AxiosResponse = await api.post("/chamados/getGeneralList", {
        ...body,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        console.log("erro --->", error);

        return error.response?.status;
      }
    }
  };

  updateChamado = async (
    body: AuthBodyType,
    chamado: string | boolean, //id do chamado
    novaMensagem: {
      novaMensagem: {
        comentario?: string;
        dataAcompanhamento: number | Date;
        status: string;
        referenciaFoto?: string[];
        referenciaArquivo?: objectModel[];
      };
    }
  ) => {
    try {
      const data: AxiosResponse = await api.post("/chamados/updateChamado", {
        ...body,
        chamado,
        ...novaMensagem,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        console.log("erro --->", error);

        return error.response?.status;
      }
    }
  };

  getTicketById = async (
    body: AuthBodyType,
    chamadoId: string //id do chamado
  ) => {
    try {
      const data: AxiosResponse = await apiv2.get(`chamados/getDetails/${encodeURIComponent(chamadoId)}`, {
        headers: { ...body },
      });
      return data.data?.success ? data.data.data : undefined;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        console.log("erro --->", error);

        return undefined;
      }
    }
  };

  redirecionaChamado = async (
    body: AuthBodyType,
    chamadoId: string | boolean, //id do chamado
    motivoEncaminhamento: string | null,
    encaminhadoPara: string | null,
    action: "redireciona" | "aceita" | "rejeita"
  ) => {
    try {
      const data: AxiosResponse = await api.post("/chamados/redirecionaChamado", {
        ...body,
        chamadoId,
        motivoEncaminhamento,
        encaminhadoPara,
        action,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        console.log("erro --->", error);

        return error.response?.status;
      }
    }
  };

  getFiltersForWeb = async (body: AuthBodyType) => {
    try {
      const data: AxiosResponse = await api.post("/chamados/getFiltersForWeb", {
        ...body,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        console.log("erro --->", error);

        return error.response?.status;
      }
    }
  };

  getChamadosDetalhe = async (body: AuthBodyType, timestamp: number) => {
    try {
      const data: AxiosResponse = await api.post("/chamados/getTicketsByFormId", {
        ...body,
        timestamp,
      });
      return data.data;
    } catch (err) {
      const error = err as Error | AxiosError;
      if (error instanceof AxiosError) {
        console.error(error.message);
        console.log("erro --->", error);
        return error.response?.status;
      }
    }
  };
}

export default Tickets;
